import { useEffect, useRef, useState } from 'react';

import { Chip, CircularProgress, Popover, styled, Typography } from '@/ui';

import checkIcon from './icons/check.svg';
import closeIcon from './icons/close.svg';
import moreVertIcon from './icons/more_vert.svg';

import type { CohortTag, CompanyCohortTag } from '@/store/api/cohortTagApi';
import type { User } from '@/types';
import type { FC, MouseEvent } from 'react';

import {
  useAddMemberCohortTagMutation,
  useCreateCohortTagMutation,
  useDeleteCohortTagMutation,
  useGetCompanyCohortTagsQuery,
  useRemoveMemberCohortTagMutation,
  useRenameCohortTagMutation,
} from '@/store/api/cohortTagApi';
import { useGetCurrentUserQuery } from '@/store/api/userApi';
import { getCompanyAdminCompanyIds, hasGlobalRole } from '@/types/user';

interface CohortTagsCellProps {
  companyId: string;
  editable: boolean;
  tags: CohortTag[];
  userId: string;
}

const figmaText = {
  fontWeight: 500,
  letterSpacing: '0.15px',
  lineHeight: 1.5,
} as const;

// Layout, colors, and icons from Figma section 2200:7066 (Add cohort frames, edit
// tag 2264:14946). Text is scaled up from Figma's 10px/6px so tag names match the
// 12px chips and stay readable; the menu widens to fit real names.
const figma = {
  addLink: {
    color: '#5C5C5C',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
  },
  button: {
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    height: 24,
    lineHeight: '16px',
    minWidth: 43,
    padding: '4px 10px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  divider: { borderTop: '1px solid #E3E3E3', width: '100%' },
  editFieldRow: { padding: '6px 0 6px 2px' },
  editInput: {
    ...figmaText,
    border: '1px solid #E3E3E3',
    borderRadius: 7,
    color: '#5C5C5C',
    fontSize: 12,
    height: 32,
    padding: 7,
    width: '100%',
  },
  editText: { ...figmaText, color: '#5C5C5C', fontSize: 12 },
  errorText: { ...figmaText, color: '#A33D1D', fontSize: 12, paddingLeft: 2 },
  header: { gap: 7, height: 32 },
  icon13: { display: 'block', height: 13, width: 13 },
  icon16: { display: 'block', height: 16, width: 16 },
  paper: {
    borderRadius: 15,
    boxShadow:
      '0px 3px 3px -2px rgba(0,0,0,0.1), 0px 3px 4px 0px rgba(0,0,0,0.1), 0px 1px 8px 0px rgba(0,0,0,0.1)',
    padding: 5,
    width: 260,
  },
  row: { height: 28 },
  rowMeta: {
    ...figmaText,
    color: '#A4A4A4',
    fontSize: 10,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  rowText: { ...figmaText, color: '#5C5C5C', fontSize: 12 },
  searchInput: {
    borderRadius: 15,
    color: '#262626',
    flex: '1 1 0',
    fontSize: 12,
    fontWeight: 500,
    height: 27,
    lineHeight: '16px',
    minWidth: 0,
    padding: '0 15px',
  },
} as const;

const TagChip = styled(Chip)({
  height: 24,
  // Pull the pill into the cell padding so chip text lines up with "Add" and the header.
  marginLeft: -10,
  // Show full tag names; the column widens instead of truncating chips.
  maxWidth: 'none',
  padding: '3px 4px',
  '& .MuiChip-label': {
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.16px',
    lineHeight: '18px',
    padding: '0 6px',
  },
  '& .MuiChip-deleteIcon': { fontSize: 16, margin: 0 },
});

const normalizeName = (name: string) => name.trim().toLowerCase();

const memberCountLabel = (count: number) =>
  `${count} ${count === 1 ? 'member' : 'members'}`;

// Mirrors CompanyCohortTagPolicy: global admins, or company admins of the row's company.
export const canEditCohortTags = (user: User | undefined, companyId: string) =>
  Boolean(user) &&
  (hasGlobalRole(user as User, 'admin') ||
    getCompanyAdminCompanyIds(user as User).includes(companyId));

const errorMessage = (error: unknown) => {
  const data = (error as { data?: { errors?: string[] } } | undefined)?.data;
  return data?.errors?.join(', ') || 'Could not save. Try again.';
};

const stopPropagation = (event: MouseEvent) => event.stopPropagation();

const CohortTagsCell: FC<CohortTagsCellProps> = ({
  companyId,
  editable,
  tags,
  userId,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [editingTag, setEditingTag] = useState<CompanyCohortTag | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // The search is focused when the menu finishes opening; the name field when editing starts.
  useEffect(() => {
    if (editingTag) editInputRef.current?.focus();
  }, [editingTag, confirmingDelete]);

  const { data: companyTags = [], isLoading } = useGetCompanyCohortTagsQuery(
    companyId,
    { skip: !anchorEl },
  );
  const [addMemberTag] = useAddMemberCohortTagMutation();
  const [removeMemberTag] = useRemoveMemberCohortTagMutation();
  const [createTag] = useCreateCohortTagMutation();
  const [renameTag] = useRenameCohortTagMutation();
  const [deleteTag] = useDeleteCohortTagMutation();

  // Chips update immediately; the members-table refetch later confirms them.
  const [shownTags, setShownTags] = useState(tags);
  const pendingTagIds = useRef(new Set<string>());
  const tagsKey = tags.map((tag) => `${tag.id}:${tag.name}`).join(',');
  useEffect(() => {
    setShownTags(tags);
  }, [tagsKey]);

  const assignedIds = new Set(shownTags.map((tag) => tag.id));
  const query = normalizeName(search);
  const visibleTags = companyTags.filter((tag) =>
    normalizeName(tag.name).includes(query),
  );
  const canCreate =
    query.length > 0 &&
    !companyTags.some((tag) => normalizeName(tag.name) === query);

  const closeEditor = () => {
    setEditingTag(null);
    setEditError(null);
    setConfirmingDelete(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
    setActionError(null);
    closeEditor();
  };

  const runAction = async (action: () => Promise<unknown>) => {
    try {
      setActionError(null);
      await action();
    } catch (error) {
      console.error('Cohort tag update failed', error);
      setActionError(errorMessage(error));
    }
  };

  // Applies the change optimistically, ignores repeat clicks while it saves,
  // and restores the previous chips if the save fails.
  const setAssigned = async (tag: CohortTag, assign: boolean) => {
    if (pendingTagIds.current.has(tag.id)) return;
    pendingTagIds.current.add(tag.id);
    // Applies or undoes only this tag, so concurrent changes to other tags survive.
    const apply = (add: boolean) =>
      setShownTags((current) =>
        add
          ? [...current.filter((t) => t.id !== tag.id), tag].sort((a, b) =>
              a.name.localeCompare(b.name),
            )
          : current.filter((t) => t.id !== tag.id),
      );
    apply(assign);
    const request = { companyId, tagId: tag.id, userId };
    try {
      setActionError(null);
      await (assign
        ? addMemberTag(request).unwrap()
        : removeMemberTag(request).unwrap());
    } catch (error) {
      console.error('Cohort tag update failed', error);
      apply(!assign);
      setActionError(errorMessage(error));
    } finally {
      pendingTagIds.current.delete(tag.id);
    }
  };

  const toggleTag = (tag: CohortTag) =>
    setAssigned(tag, !assignedIds.has(tag.id));

  const handleCreate = () =>
    runAction(async () => {
      const tag = await createTag({ companyId, name: search }).unwrap();
      setSearch('');
      await setAssigned({ id: tag.id, name: tag.name }, true);
    });

  const openEditor = (tag: CompanyCohortTag) => {
    setSearch('');
    setEditingTag(tag);
    setEditName(tag.name);
    setEditError(null);
    setConfirmingDelete(false);
  };

  const handleRename = async () => {
    if (!editingTag) return;
    try {
      await renameTag({
        companyId,
        name: editName,
        tagId: editingTag.id,
      }).unwrap();
      closeEditor();
    } catch (error) {
      setEditError(errorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!editingTag) return;
    try {
      await deleteTag({ companyId, tagId: editingTag.id }).unwrap();
      closeEditor();
    } catch (error) {
      setConfirmingDelete(false);
      setEditError(errorMessage(error));
    }
  };

  const renderSearchHeader = () => (
    <>
      <div className="flex items-center" style={figma.header}>
        <input
          ref={searchInputRef}
          aria-label="Search cohorts"
          className="border-none bg-white outline-none placeholder:text-cement-300"
          placeholder="Search or create cohort"
          style={figma.searchInput}
          value={search}
          onChange={(event) => {
            closeEditor();
            setSearch(event.target.value);
          }}
        />
        <button
          aria-label="Close"
          className="flex cursor-pointer border-none bg-transparent p-0"
          type="button"
          onClick={handleClose}
        >
          <img alt="" src={closeIcon} style={figma.icon13} />
        </button>
      </div>
      <div style={figma.divider} />
    </>
  );

  const renderEditor = (tag: CompanyCohortTag) => (
    <div className="flex flex-col items-end" style={{ gap: 12 }}>
      <div className="flex w-full flex-col" style={figma.editFieldRow}>
        {confirmingDelete ? (
          <p className="m-0" style={figma.editText}>
            {`Remove "${tag.name}" from ${memberCountLabel(tag.memberCount)}?`}
          </p>
        ) : (
          <input
            ref={editInputRef}
            aria-label="Tag name"
            className="outline-none"
            style={figma.editInput}
            value={editName}
            onChange={(event) => {
              setEditError(null);
              setEditName(event.target.value);
            }}
          />
        )}
        {editError && (
          <p className="m-0" role="alert" style={figma.errorText}>
            {editError}
          </p>
        )}
      </div>
      <div className="flex w-full items-center justify-between">
        <button
          className="cursor-pointer border-none bg-transparent"
          style={{ ...figma.button, color: '#A33D1D' }}
          type="button"
          onClick={
            confirmingDelete ? handleDelete : () => setConfirmingDelete(true)
          }
        >
          Delete
        </button>
        <div className="flex items-center" style={{ gap: 10 }}>
          <button
            className="cursor-pointer border-none bg-transparent"
            style={{ ...figma.button, color: '#262626' }}
            type="button"
            onClick={
              confirmingDelete ? () => setConfirmingDelete(false) : closeEditor
            }
          >
            Cancel
          </button>
          {!confirmingDelete && (
            <button
              className="cursor-pointer border-none"
              style={{
                ...figma.button,
                backgroundColor: '#637A6F',
                color: '#FFFFFF',
              }}
              type="button"
              onClick={handleRename}
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Bolds the part of a tag name that matches the search, as in the Figma typing frames.
  const renderName = (name: string) => {
    const start = query ? name.toLowerCase().indexOf(query) : -1;
    if (start < 0) return name;
    const end = start + query.length;

    return (
      <>
        {name.slice(0, start)}
        <span style={{ fontWeight: 700 }}>{name.slice(start, end)}</span>
        {name.slice(end)}
      </>
    );
  };

  const renderTagRow = (tag: CompanyCohortTag) => {
    const isAssigned = assignedIds.has(tag.id);

    return (
      <li
        key={tag.id}
        className="flex items-center justify-between"
        style={{ ...figma.row, paddingLeft: isAssigned ? 0 : 10 }}
      >
        <button
          className="flex min-w-0 flex-1 cursor-pointer items-center justify-between border-none bg-transparent p-0 text-left"
          type="button"
          onClick={() => toggleTag(tag)}
        >
          {isAssigned && <img alt="" src={checkIcon} style={figma.icon13} />}
          <span
            className="min-w-0 flex-1 truncate"
            style={{ ...figma.rowText, paddingLeft: isAssigned ? 4 : 0 }}
            title={tag.name}
          >
            {renderName(tag.name)}
          </span>
          <span
            style={{
              ...figma.rowMeta,
              paddingLeft: 10,
              textDecoration: isAssigned ? 'underline' : 'none',
            }}
          >
            {isAssigned ? 'Remove' : memberCountLabel(tag.memberCount)}
          </span>
        </button>
        <button
          aria-label={`Edit ${tag.name}`}
          className="flex cursor-pointer border-none bg-transparent p-0"
          style={{ marginLeft: 8 }}
          type="button"
          onClick={() => openEditor(tag)}
        >
          <img alt="" src={moreVertIcon} style={figma.icon16} />
        </button>
      </li>
    );
  };

  const renderList = () => (
    <>
      {isLoading ? (
        <CircularProgress className="m-2 self-center" size={16} />
      ) : (
        <ul className="m-0 flex max-h-64 list-none flex-col overflow-y-auto p-0">
          {visibleTags.map(renderTagRow)}
          {canCreate && (
            <li
              className="flex items-center"
              style={{ ...figma.row, padding: '0 10px' }}
            >
              <button
                className="cursor-pointer border-none bg-transparent p-0 text-left"
                style={{ ...figma.rowText, fontWeight: 700 }}
                type="button"
                onClick={handleCreate}
              >
                {`+ Create \u201c${search.trim()}\u201d`}
              </button>
            </li>
          )}
        </ul>
      )}
      {actionError && (
        <p className="m-0" role="alert" style={figma.errorText}>
          {actionError}
        </p>
      )}
    </>
  );

  if (!editable && shownTags.length === 0) {
    return (
      <Typography className="!text-sm" color="text.disabled" variant="body2">
        —
      </Typography>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stops clicks, including those bubbling from the popover portal, from navigating the row.
    <div
      className="flex flex-col items-start"
      style={{ gap: 5 }}
      onClick={stopPropagation}
    >
      {shownTags.map((tag) => (
        <TagChip
          key={tag.id}
          label={tag.name}
          size="small"
          onDelete={editable ? () => setAssigned(tag, false) : undefined}
        />
      ))}
      {editable && (
        <button
          className="cursor-pointer border-none bg-transparent p-0 underline"
          style={figma.addLink}
          type="button"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          Add
        </button>
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={Boolean(anchorEl)}
        slotProps={{ paper: { style: figma.paper } }}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        disableAutoFocus
        onClose={handleClose}
      >
        {renderSearchHeader()}
        {editingTag ? renderEditor(editingTag) : renderList()}
      </Popover>
    </div>
  );
};

// Table entry point: derives edit permission from the signed-in viewer.
export const MemberCohortTagsCell: FC<
  Omit<CohortTagsCellProps, 'editable'>
> = ({ companyId, tags, userId }) => {
  const { data: currentUser } = useGetCurrentUserQuery();

  return (
    <CohortTagsCell
      companyId={companyId}
      editable={canEditCohortTags(currentUser, companyId)}
      tags={tags}
      userId={userId}
    />
  );
};

export default CohortTagsCell;
